import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, X, HeartPulse } from 'lucide-react';
import { driversAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const WellbeingDueBanner = () => {
  const location = useLocation();
  const envPrefix = location.pathname.startsWith('/test') ? '/test' : '';
  const { isSupervisor, isAdmin } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWellbeingDue = async () => {
      if (!isSupervisor && !isAdmin) {
        setLoading(false);
        return;
      }

      try {
        const response = await driversAPI.getWellbeingDueBanners();
        setDrivers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch wellbeing due data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWellbeingDue();
  }, [isSupervisor, isAdmin]);

  if (loading || dismissed || drivers.length === 0 || (!isSupervisor && !isAdmin)) {
    return null;
  }

  const criticalCount = drivers.filter(d => d.urgency === 'critical').length;
  const highCount = drivers.filter(d => d.urgency === 'high').length;

  const getBannerColor = () => {
    if (criticalCount > 0) return 'bg-purple-700';
    if (highCount > 0) return 'bg-purple-500';
    return 'bg-indigo-500';
  };

  const getMessage = () => {
    if (criticalCount > 0) {
      return `${criticalCount} driver(s) have Wellbeing Check due TODAY or overdue!`;
    }
    if (highCount > 0) {
      return `${highCount} driver(s) have Wellbeing Check due within 7 days`;
    }
    return `${drivers.length} driver(s) have Wellbeing Check due soon`;
  };

  return (
    <div className={`${getBannerColor()} text-white px-4 py-2`} data-testid="wellbeing-due-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HeartPulse className="w-5 h-5" />
          <span className="font-medium">{getMessage()}</span>
          <Link 
            to={`${envPrefix}/drivers`}
            className="underline hover:no-underline font-semibold ml-2"
          >
            View Drivers
          </Link>
        </div>
        <button 
          onClick={() => setDismissed(true)}
          className="hover:bg-white/20 rounded p-1"
          aria-label="Dismiss banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default WellbeingDueBanner;
